'use client';

import { useState, useEffect } from 'react';
import { X, Save, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { DiscoveryGoal, DiscoveryInterview, DiscoveryCompetitor, DiscoveryType } from '@/hooks/use-discovery';

interface EditRowDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: Record<string, unknown>) => Promise<void>;
  type: DiscoveryType;
  row: DiscoveryGoal | DiscoveryInterview | DiscoveryCompetitor | null;
  isLoading?: boolean;
}

export function EditRowDialog({
  isOpen,
  onClose,
  onSave,
  type,
  row,
  isLoading = false,
}: EditRowDialogProps) {
  const [formData, setFormData] = useState<Record<string, string | null>>({});

  useEffect(() => {
    if (row) {
      // Initialize form data based on row type
      if (type === 'goals') {
        const goal = row as DiscoveryGoal;
        setFormData({
          title: goal.title || '',
          description: goal.description || '',
          successMetrics: goal.successMetrics || '',
          timeline: goal.timeline || '',
          category: goal.category || '',
          priority: goal.priority || '',
        });
      } else if (type === 'interviews') {
        const interview = row as DiscoveryInterview;
        setFormData({
          stakeholderName: interview.stakeholderName || '',
          stakeholderTitle: interview.stakeholderTitle || '',
          stakeholderOrg: interview.stakeholderOrg || '',
          interviewDate: interview.interviewDate ? new Date(interview.interviewDate).toISOString().split('T')[0] : '',
          keyInsights: interview.keyInsights || '',
          painPoints: interview.painPoints || '',
          influence: interview.influence || '',
          fullNotes: interview.fullNotes || '',
        });
      } else if (type === 'competitors') {
        const competitor = row as DiscoveryCompetitor;
        setFormData({
          name: competitor.name || '',
          category: competitor.category || '',
          threatLevel: competitor.threatLevel || '',
          marketPosition: competitor.marketPosition || '',
          strengths: competitor.strengths || '',
          weaknesses: competitor.weaknesses || '',
          marketShare: competitor.marketShare || '',
          marketShareSource: competitor.marketShareSource || '',
          sourceLinks: Array.isArray(competitor.sourceLinks) ? (competitor.sourceLinks as string[]).join('\n') : '',
        });
      }
    }
  }, [row, type]);

  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Transform data for competitors - convert source links string to array
    const dataToSave = { ...formData };
    if (type === 'competitors' && formData.sourceLinks) {
      dataToSave.sourceLinks = (formData.sourceLinks as string)
        .split('\n')
        .map(link => link.trim())
        .filter(link => link.length > 0) as any;
    }
    
    await onSave(dataToSave);
  };

  if (!isOpen) return null;

  const renderGoalFields = () => (
    <>
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">Title *</label>
        <input
          type="text"
          value={formData.title || ''}
          onChange={(e) => handleChange('title', e.target.value)}
          className="w-full px-4 py-2.5 border border-gray-300 rounded-[10px] bg-white focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary"
          required
        />
      </div>
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">Description</label>
        <textarea
          value={formData.description || ''}
          onChange={(e) => handleChange('description', e.target.value)}
          rows={5}
          className="w-full px-4 py-2.5 border border-gray-300 rounded-[10px] bg-white focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary resize-none"
        />
      </div>
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">Success Metrics</label>
        <textarea
          value={formData.successMetrics || ''}
          onChange={(e) => handleChange('successMetrics', e.target.value)}
          rows={2}
          placeholder="e.g., Increase NPS by 20 points, Reduce churn by 15%"
          className="w-full px-4 py-2.5 border border-gray-300 rounded-[10px] bg-white focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary resize-none"
        />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">Timeline</label>
          <input
            type="text"
            value={formData.timeline || ''}
            onChange={(e) => handleChange('timeline', e.target.value)}
            placeholder="e.g., Q1 2025"
            className="w-full px-4 py-2.5 border border-gray-300 rounded-[10px] bg-white focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary"
          />
        </div>
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">Category</label>
          <Select value={formData.category || ''} onValueChange={(value) => handleChange('category', value)}>
            <SelectTrigger className="w-full h-11 rounded-[10px] border-gray-300 bg-white">
              <SelectValue placeholder="Select category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="BUSINESS">Business</SelectItem>
              <SelectItem value="USER">User</SelectItem>
              <SelectItem value="PRODUCT">Product</SelectItem>
              <SelectItem value="TECHNICAL">Technical</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">Priority</label>
        <Select value={formData.priority || ''} onValueChange={(value) => handleChange('priority', value)}>
          <SelectTrigger className="w-full h-11 rounded-[10px] border-gray-300 bg-white">
            <SelectValue placeholder="Select priority" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="CRITICAL">Critical</SelectItem>
            <SelectItem value="HIGH">High</SelectItem>
            <SelectItem value="MEDIUM">Medium</SelectItem>
            <SelectItem value="LOW">Low</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </>
  );

  const renderInterviewFields = () => (
    <>
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">Stakeholder Name *</label>
        <input
          type="text"
          value={formData.stakeholderName || ''}
          onChange={(e) => handleChange('stakeholderName', e.target.value)}
          className="w-full px-4 py-2.5 border border-gray-300 rounded-[10px] bg-white focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary"
          required
        />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">Title</label>
          <input
            type="text"
            value={formData.stakeholderTitle || ''}
            onChange={(e) => handleChange('stakeholderTitle', e.target.value)}
            placeholder="e.g., VP of Product"
            className="w-full px-4 py-2.5 border border-gray-300 rounded-[10px] bg-white focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary"
          />
        </div>
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">Organization</label>
          <input
            type="text"
            value={formData.stakeholderOrg || ''}
            onChange={(e) => handleChange('stakeholderOrg', e.target.value)}
            placeholder="e.g., Product Team"
            className="w-full px-4 py-2.5 border border-gray-300 rounded-[10px] bg-white focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary"
          />
        </div>
      </div>
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">Interview Date</label>
        <input
          type="date"
          value={formData.interviewDate || ''}
          onChange={(e) => handleChange('interviewDate', e.target.value)}
          className="w-full px-4 py-2.5 border border-gray-300 rounded-[10px] bg-white focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary"
        />
      </div>
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">Key Insights</label>
        <textarea
          value={formData.keyInsights || ''}
          onChange={(e) => handleChange('keyInsights', e.target.value)}
          rows={4}
          placeholder="Main takeaways from the interview"
          className="w-full px-4 py-2.5 border border-gray-300 rounded-[10px] bg-white focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary resize-none"
        />
      </div>
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">Pain Points</label>
        <textarea
          value={formData.painPoints || ''}
          onChange={(e) => handleChange('painPoints', e.target.value)}
          rows={3}
          placeholder="Challenges and concerns mentioned"
          className="w-full px-4 py-2.5 border border-gray-300 rounded-[10px] bg-white focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary resize-none"
        />
      </div>
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">Influence Level</label>
        <Select value={formData.influence || ''} onValueChange={(value) => handleChange('influence', value)}>
          <SelectTrigger className="w-full h-11 rounded-[10px] border-gray-300 bg-white">
            <SelectValue placeholder="Select influence level" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="HIGH">High</SelectItem>
            <SelectItem value="MEDIUM">Medium</SelectItem>
            <SelectItem value="LOW">Low</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">Full Notes</label>
        <textarea
          value={formData.fullNotes || ''}
          onChange={(e) => handleChange('fullNotes', e.target.value)}
          rows={5}
          placeholder="Complete interview notes..."
          className="w-full px-4 py-2.5 border border-gray-300 rounded-[10px] bg-white focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary resize-none"
        />
      </div>
    </>
  );

  const renderCompetitorFields = () => (
    <>
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">Competitor Name *</label>
        <input
          type="text"
          value={formData.name || ''}
          onChange={(e) => handleChange('name', e.target.value)}
          className="w-full px-4 py-2.5 border border-gray-300 rounded-[10px] bg-white focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary"
          required
        />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">Category</label>
          <Select value={formData.category || ''} onValueChange={(value) => handleChange('category', value)}>
            <SelectTrigger className="w-full h-11 rounded-[10px] border-gray-300 bg-white">
              <SelectValue placeholder="Select category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="DIRECT">Direct</SelectItem>
              <SelectItem value="INDIRECT">Indirect</SelectItem>
              <SelectItem value="SUBSTITUTE">Substitute</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">Threat Level</label>
          <Select value={formData.threatLevel || ''} onValueChange={(value) => handleChange('threatLevel', value)}>
            <SelectTrigger className="w-full h-11 rounded-[10px] border-gray-300 bg-white">
              <SelectValue placeholder="Select threat level" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="HIGH">High</SelectItem>
              <SelectItem value="MEDIUM">Medium</SelectItem>
              <SelectItem value="LOW">Low</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">Market Position</label>
        <textarea
          value={formData.marketPosition || ''}
          onChange={(e) => handleChange('marketPosition', e.target.value)}
          rows={3}
          placeholder="Describe their market position..."
          className="w-full px-4 py-2.5 border border-gray-300 rounded-[10px] bg-white focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary resize-none"
        />
      </div>
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">Strengths</label>
        <textarea
          value={formData.strengths || ''}
          onChange={(e) => handleChange('strengths', e.target.value)}
          rows={3}
          placeholder="Key advantages and strengths"
          className="w-full px-4 py-2.5 border border-gray-300 rounded-[10px] bg-white focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary resize-none"
        />
      </div>
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">Weaknesses</label>
        <textarea
          value={formData.weaknesses || ''}
          onChange={(e) => handleChange('weaknesses', e.target.value)}
          rows={3}
          placeholder="Key disadvantages and weaknesses"
          className="w-full px-4 py-2.5 border border-gray-300 rounded-[10px] bg-white focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary resize-none"
        />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">Market Share</label>
          <input
            type="text"
            value={formData.marketShare || ''}
            onChange={(e) => handleChange('marketShare', e.target.value)}
            placeholder="e.g., ~15%"
            className="w-full px-4 py-2.5 border border-gray-300 rounded-[10px] bg-white focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary"
          />
        </div>
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">Source</label>
          <input
            type="text"
            value={formData.marketShareSource || ''}
            onChange={(e) => handleChange('marketShareSource', e.target.value)}
            placeholder="e.g., Gartner 2024"
            className="w-full px-4 py-2.5 border border-gray-300 rounded-[10px] bg-white focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary"
          />
        </div>
      </div>
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">Source Links</label>
        <textarea
          value={formData.sourceLinks || ''}
          onChange={(e) => handleChange('sourceLinks', e.target.value)}
          rows={2}
          placeholder="Enter URLs, one per line"
          className="w-full px-4 py-2.5 border border-gray-300 rounded-[10px] bg-white focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary resize-none"
        />
        <p className="text-xs text-gray-500">Enter each URL on a new line</p>
      </div>
    </>
  );

  const getTitle = () => {
    const isNew = !row;
    switch (type) {
      case 'goals':
        return isNew ? 'Add Goal' : 'Edit Goal';
      case 'interviews':
        return isNew ? 'Add Interview' : 'Edit Interview';
      case 'competitors':
        return isNew ? 'Add Competitor' : 'Edit Competitor';
      default:
        return 'Edit';
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
      />
      
      {/* Dialog */}
      <div className="relative bg-white rounded-2xl shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">{getTitle()}</h2>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
          <div className="p-6 space-y-4">
            {type === 'goals' && renderGoalFields()}
            {type === 'interviews' && renderInterviewFields()}
            {type === 'competitors' && renderCompetitorFields()}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="h-10 px-5 text-gray-700 hover:bg-gray-100 rounded-[10px] transition-colors font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className={cn(
                "h-10 px-5 flex items-center gap-2 rounded-[10px] text-white transition-colors font-medium",
                isLoading
                  ? "bg-brand-primary/70 cursor-not-allowed"
                  : "bg-brand-primary hover:bg-brand-primary/90"
              )}
            >
              {isLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              {isLoading ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

